import { ComponentProps, FC, ReactNode } from 'react'
import {
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text
} from '@chakra-ui/react'

import AmSpinner from '@/components/atoms/AmSpinner'
import { maxContainerWidth, mobileBreakpoint } from '@/constants'
import { useAmMediaQuery } from '@/hooks'

interface Props {
  open: boolean
  onClose: () => void
  children: ReactNode
  title?: string
  subTitle?: string
  primaryBtnProps?: ComponentProps<typeof Button>
  secondaryBtnProps?: ComponentProps<typeof Button>
  headerBtnProps?: ComponentProps<typeof Button>
  size?: string
  showCloseButton?: boolean
  isLoading?: boolean
  minHeight?: string
  maxHeight?: string
  modalBodyCustomProps?: ComponentProps<typeof ModalBody>
}

const AmModal: FC<Props> = ({
  open,
  onClose,
  children,
  title,
  subTitle = '',
  primaryBtnProps,
  secondaryBtnProps,
  headerBtnProps,
  size = 'md',
  showCloseButton = true,
  isLoading = false,
  minHeight = '',
  maxHeight = '',
  modalBodyCustomProps = {}
}) => {
  const isMobile = useAmMediaQuery(mobileBreakpoint)

  const headerMobileStyle = {
    borderBottom: '1px solid',
    borderColor: 'brand.lightGrey'
  }

  return (
    <Modal
      isCentered
      motionPreset={isMobile ? 'none' : 'scale'}
      scrollBehavior='inside'
      isOpen={open}
      onClose={onClose}
      size={size}
      closeOnEsc={false}
      closeOnOverlayClick={false}
    >
      <ModalOverlay />

      {isLoading && <AmSpinner absolute />}

      <ModalContent
        sx={{ display: isLoading ? 'none' : 'flex' }}
        m={size !== 'full' && isMobile ? 5 : 0}
        maxWidth={size === 'full' ? maxContainerWidth : undefined}
        minHeight={minHeight || undefined}
        maxHeight={maxHeight || undefined}
      >
        <ModalHeader
          {...(isMobile && size === 'full' ? { ...headerMobileStyle } : {})}
          pr={isMobile && size === 'full' && showCloseButton ? 10 : 0}
        >
          {title}
          {subTitle && (
            <Text variant='secondary' mt={3} lineHeight='20px'>
              {subTitle}
            </Text>
          )}
        </ModalHeader>
        {showCloseButton && <ModalCloseButton mt='6px' />}
        {headerBtnProps && (
          <Button
            position='absolute'
            top='0.75rem'
            right='0.75rem'
            {...headerBtnProps}
          ></Button>
        )}
        <ModalBody pt={5} {...modalBodyCustomProps}>{children}</ModalBody>
        {(primaryBtnProps || secondaryBtnProps) && (
          <ModalFooter>
            {secondaryBtnProps && <Button {...secondaryBtnProps}></Button>}
            {primaryBtnProps && <Button {...primaryBtnProps}></Button>}
          </ModalFooter>
        )}
      </ModalContent>
    </Modal>
  )
}

export default AmModal
